import { useState, useEffect, useRef } from 'react'

const resolveInitial = <T,>(initialState: T | (() => T)): T =>
    initialState instanceof Function ? (initialState as () => T)() : initialState

const readFromStorage = <T,>(key: string, initialState: T | (() => T)): T => {
    try {
        const item = sessionStorage.getItem(key)
        if (item !== null) return JSON.parse(item) as T
    } catch {
    }
    return resolveInitial(initialState)
}

export function usePersistedState<T>(key: string, initialState: T | (() => T)): [T, (value: T | ((val: T) => T)) => void] {
    const initialStateRef = useRef(initialState)
    initialStateRef.current = initialState

    const lastSyncedKeyRef = useRef<string>(key)

    const [state, setState] = useState<T>(() => readFromStorage(key, initialState))

    if (lastSyncedKeyRef.current !== key) {
        lastSyncedKeyRef.current = key
        setState(readFromStorage(key, initialStateRef.current))
    }

    useEffect(() => {
        if (lastSyncedKeyRef.current !== key) return
       try {
            sessionStorage.setItem(key, JSON.stringify(state))
        } catch {
        }
    }, [key, state])

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === key && event.newValue !== null) {
                try {
                    setState(JSON.parse(event.newValue))
                } catch {
                }
            }
        }

        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [key])

    return [state, setState]
}
